/**
 * Affiliate Payouts Page
 * Request payouts and view payout history
 */

import { useState, useEffect } from 'react';
import { affiliateApi } from '../../api/affiliate';
import PayoutRequestForm from '../../components/affiliate/PayoutRequestForm';
import PayoutHistory from '../../components/affiliate/PayoutHistory';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import ErrorMessage from '../../components/ui/ErrorMessage';

export default function AffiliatePayouts() {
  const [availableForPayout, setAvailableForPayout] = useState<any>(null);
  const [payouts, setPayouts] = useState<any[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showRequestForm, setShowRequestForm] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load all data in parallel
      const [availableData, payoutsData, statsData] = await Promise.all([
        affiliateApi.getAvailableForPayout(),
        affiliateApi.getPayouts({ limit: 10 }),
        affiliateApi.getPayoutStats(),
      ]);

      setAvailableForPayout(availableData);
      setPayouts(payoutsData.payouts);
      setStats(statsData.stats);
    } catch (err: any) {
      setError(err.message || 'Failed to load payout data');
    } finally {
      setLoading(false);
    }
  };

  const handlePayoutRequested = () => {
    setShowRequestForm(false);
    loadData(); // Reload data
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <ErrorMessage message={error} onRetry={loadData} />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">Payouts</h1>
          <p className="text-slate-400 mt-1">
            Request payouts and view payout history
          </p>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-gradient-to-br from-green-600 to-green-700 rounded-lg p-6">
          <div className="text-green-200 mb-2">
            <svg
              className="w-8 h-8"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <p className="text-3xl font-bold text-white">
            ${availableForPayout?.totalAmount?.toFixed(2) || '0.00'}
          </p>
          <p className="text-sm text-green-200 mt-1">Available for Payout</p>
          <p className="text-xs text-green-300 mt-1">
            {availableForPayout?.count || 0} approved commissions
          </p>
        </div>

        <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-lg p-6">
          <div className="text-blue-200 mb-2">
            <svg
              className="w-8 h-8"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
            </svg>
          </div>
          <p className="text-3xl font-bold text-white">
            ${stats?.totalPaid?.toFixed(2) || '0.00'}
          </p>
          <p className="text-sm text-blue-200 mt-1">Total Paid</p>
          <p className="text-xs text-blue-300 mt-1">
            {stats?.completedPayouts || 0} payouts
          </p>
        </div>

        <div className="bg-gradient-to-br from-yellow-600 to-yellow-700 rounded-lg p-6">
          <div className="text-yellow-200 mb-2">
            <svg
              className="w-8 h-8"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <p className="text-3xl font-bold text-white">
            ${stats?.pendingAmount?.toFixed(2) || '0.00'}
          </p>
          <p className="text-sm text-yellow-200 mt-1">Pending Payouts</p>
          <p className="text-xs text-yellow-300 mt-1">
            {stats?.pendingPayouts || 0} requests
          </p>
        </div>

        <div className="bg-gradient-to-br from-purple-600 to-purple-700 rounded-lg p-6">
          <div className="text-purple-200 mb-2">
            <svg
              className="w-8 h-8"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
          <p className="text-2xl font-bold text-white">
            {stats?.lastPayoutDate
              ? new Date(stats.lastPayoutDate).toLocaleDateString()
              : 'Never'}
          </p>
          <p className="text-sm text-purple-200 mt-1">Last Payout</p>
        </div>
      </div>

      {/* Request Payout Section */}
      <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl border border-slate-700/50 p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-semibold text-white">Request Payout</h2>
            <p className="text-sm text-slate-400 mt-1">
              Minimum payout amount: $50.00
            </p>
          </div>
          {!showRequestForm && (
            <button
              onClick={() => setShowRequestForm(true)}
              disabled={(availableForPayout?.totalAmount || 0) < 50}
              className="px-6 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Request Payout
            </button>
          )}
        </div>

        {showRequestForm ? (
          <PayoutRequestForm
            availableAmount={availableForPayout?.totalAmount || 0}
            onSuccess={handlePayoutRequested}
            onCancel={() => setShowRequestForm(false)}
          />
        ) : (
          <div className="bg-gray-700/30 rounded-lg p-4 border border-slate-600">
            <p className="text-slate-300">
              You have{' '}
              <span className="font-bold text-green-400">
                ${availableForPayout?.totalAmount?.toFixed(2) || '0.00'}
              </span>{' '}
              available for payout from{' '}
              <span className="font-bold text-orange-400">
                {availableForPayout?.count || 0}
              </span>{' '}
              approved commissions.
            </p>
            {(availableForPayout?.totalAmount || 0) < 50 && (
              <p className="text-yellow-400 text-sm mt-2">
                You need at least $50.00 to request a payout.
              </p>
            )}
          </div>
        )}
      </div>

      {/* Payout History */}
      <PayoutHistory payouts={payouts} onRefresh={loadData} />
    </div>
  );
}
